import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Eye, MapPin, Clock, Play, Camera, Home, Building, Heart, 
  Calendar, Phone, Users, ChevronRight, CheckCircle, X,
  Brain, Activity, Stethoscope, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VirtualTours = () => {
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourTypes, setTourTypes] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [visitRequest, setVisitRequest] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    party_size: 1,
    special_needs: '',
    questions: ''
  });

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/phase49/tours`);
      const data = await response.json();
      setTours(data.tours || []);
      setTourTypes(data.tour_types || []);
    } catch (error) {
      console.error('Error fetching tours:', error);
      toast.error('Failed to load virtual tours');
    } finally {
      setLoading(false);
    }
  };

  const fetchTourDetail = async (tourId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase49/tours/${tourId}`);
      const data = await response.json();
      setSelectedTour(data);
      setSelectedRoom(data.rooms?.[0] || data.sections?.[0] || null);
    } catch (error) {
      console.error('Error fetching tour detail:', error);
      toast.error('Failed to load tour details');
    }
  };

  const handleVisitRequest = async () => {
    if (!selectedTour) return;
    try {
      const response = await fetch(`${API_URL}/api/phase49/tours/${selectedTour.id}/request-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(visitRequest)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowVisitDialog(false);
        setVisitRequest({
          name: '',
          email: '',
          phone: '',
          preferred_date: '',
          preferred_time: '',
          party_size: 1,
          special_needs: '',
          questions: ''
        });
      } else {
        toast.error('Failed to submit visit request');
      }
    } catch (error) {
      console.error('Error submitting visit request:', error);
      toast.error('Failed to submit visit request');
    }
  };

  const filteredTours = filterType === 'all' 
    ? tours 
    : tours.filter(t => t.type === filterType);

  const getTourIcon = (type) => {
    switch (type) {
      case 'rehabilitation': return Activity;
      case 'home_health': return Stethoscope;
      case 'brain_health': return Brain;
      case 'hospice': return Heart;
      case 'home_care': return Home;
      case 'living_at_home': return Shield;
      default: return Building;
    }
  };

  const getTourColor = (type) => {
    switch (type) {
      case 'rehabilitation': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'home_health': return 'bg-green-100 text-green-800 border-green-200';
      case 'brain_health': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'hospice': return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'home_care': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'living_at_home': return 'bg-teal-100 text-teal-800 border-teal-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading services...</p>
        </div>
      </div>
    );
  }

  if (selectedTour) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        {/* Header */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                onClick={() => { setSelectedTour(null); setSelectedRoom(null); }}
                data-testid="back-to-tours"
              >
                <X className="h-4 w-4 mr-2" />
                Back to Services
              </Button>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setShowVisitDialog(true)}
                  data-testid="schedule-visit-btn"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Consultation
                </Button>
                <Button variant="outline" asChild>
                  <a href="tel:703-578-7195">
                    <Phone className="h-4 w-4 mr-2" />
                    703-578-7195
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Sidebar */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    {React.createElement(getTourIcon(selectedTour.type), { className: "h-6 w-6 text-teal-600" })}
                    <Badge className={getTourColor(selectedTour.type)}>
                      {tourTypes.find(t => t.id === selectedTour.type)?.name || selectedTour.type}
                    </Badge>
                  </div>
                  <CardTitle>{selectedTour.name}</CardTitle>
                  <CardDescription>{selectedTour.description}</CardDescription>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {selectedTour.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {selectedTour.location}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold text-slate-800 mb-3">Explore</h4>
                  <div className="space-y-2">
                    {(selectedTour.rooms || selectedTour.sections)?.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedRoom(item)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedRoom?.id === item.id 
                            ? 'bg-teal-50 border-teal-200' 
                            : 'hover:bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`tour-stop-${index}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                            selectedRoom?.id === item.id 
                              ? 'bg-teal-600 text-white' 
                              : 'bg-slate-200 text-slate-600'
                          }`}>
                            {index + 1}
                          </div>
                          <span className="font-medium text-sm">{item.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Services List */}
                  {selectedTour.services && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-semibold text-slate-800 mb-2">Services Include</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedTour.services.map((service, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2">
              {selectedRoom && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">{selectedRoom.name}</CardTitle>
                    <CardDescription>{selectedRoom.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* 360 View Placeholder */}
                    <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg mb-6 flex items-center justify-center">
                      <div className="text-center">
                        <Camera className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-600 font-medium">Virtual Tour</p>
                        <p className="text-slate-400 text-sm mb-4">Interactive experience</p>
                        <Button variant="outline">
                          <Play className="h-4 w-4 mr-2" />
                          Start Tour
                        </Button>
                      </div>
                    </div>

                    {/* Features */}
                    {selectedRoom.features && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3">Key Features</h4>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {selectedRoom.features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                              <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0" />
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Team Members */}
                    {selectedRoom.team_members && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3">Care Team</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedRoom.team_members.map((member, index) => (
                            <Badge key={index} variant="secondary" className="bg-teal-50 text-teal-700">
                              <Users className="h-3 w-3 mr-1" />
                              {member}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Equipment */}
                    {selectedRoom.equipment && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3">Equipment & Resources</h4>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {selectedRoom.equipment.map((item, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                              <CheckCircle className="h-4 w-4 text-teal-500" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Details */}
                    {selectedRoom.details && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-slate-600 text-sm">{selectedRoom.details}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Visit Request Dialog */}
        <Dialog open={showVisitDialog} onOpenChange={setShowVisitDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Schedule a Consultation</DialogTitle>
              <DialogDescription>
                Our team will contact you to discuss your care needs and answer questions.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="visit-name">Your Name</Label>
                <Input
                  id="visit-name"
                  value={visitRequest.name}
                  onChange={(e) => setVisitRequest({...visitRequest, name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="visit-email">Email</Label>
                  <Input
                    id="visit-email"
                    type="email"
                    value={visitRequest.email}
                    onChange={(e) => setVisitRequest({...visitRequest, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="visit-phone">Phone</Label>
                  <Input
                    id="visit-phone"
                    value={visitRequest.phone}
                    onChange={(e) => setVisitRequest({...visitRequest, phone: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="visit-questions">Questions or Comments</Label>
                <Textarea
                  id="visit-questions"
                  value={visitRequest.questions}
                  onChange={(e) => setVisitRequest({...visitRequest, questions: e.target.value})}
                  placeholder="Tell us about your care needs..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVisitDialog(false)}>Cancel</Button>
              <Button onClick={handleVisitRequest} data-testid="submit-visit-request">
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-3">
            <Eye className="h-8 w-8" />
            <h1 className="text-3xl font-bold" data-testid="virtual-tours-title">Goodwin Living Health Services</h1>
          </div>
          <p className="text-lg text-slate-300 max-w-3xl">
            World-class, compassionate health care — from rehabilitation to home health to brain health and hospice. 
            Explore our comprehensive services designed to support you at every stage of life.
          </p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <a href="tel:703-578-7195" className="hover:underline">703-578-7195</a>
            </span>
            <span className="text-slate-400">|</span>
            <span>Health@GoodwinLiving.org</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <span className="text-slate-600 font-medium">Services:</span>
          <Button 
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            All
          </Button>
          {tourTypes.map(type => (
            <Button 
              key={type.id}
              variant={filterType === type.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType(type.id)}
              className="flex items-center gap-1"
            >
              {React.createElement(getTourIcon(type.id), { className: "h-3 w-3" })}
              {type.name}
            </Button>
          ))}
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTours.map(tour => {
            const IconComponent = getTourIcon(tour.type);
            return (
              <Card 
                key={tour.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden group"
                onClick={() => fetchTourDetail(tour.id)}
                data-testid={`tour-card-${tour.id}`}
              >
                <div className="aspect-video bg-slate-200 relative overflow-hidden">
                  <img 
                    src={tour.thumbnail} 
                    alt={tour.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <Badge className={`${getTourColor(tour.type)} mb-2`}>
                      <IconComponent className="h-3 w-3 mr-1" />
                      {tourTypes.find(t => t.id === tour.type)?.name || tour.type}
                    </Badge>
                  </div>
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg leading-tight">{tour.name}</CardTitle>
                  <CardDescription className="line-clamp-2 text-sm">{tour.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {tour.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {tour.location}
                    </span>
                  </div>
                  <Button className="w-full mt-4" variant="outline" size="sm">
                    Explore Service
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredTours.length === 0 && (
          <div className="text-center py-12">
            <Eye className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No services found for this category.</p>
          </div>
        )}

        {/* Contact CTA */}
        <Card className="mt-12 bg-gradient-to-r from-teal-700 to-teal-600 text-white border-0">
          <CardContent className="py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold mb-2">Need Help Finding Care?</h3>
                <p className="text-teal-100">
                  Our team is available to discuss your options and help you explore the right care for you or your loved one.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" size="lg" asChild>
                  <a href="tel:703-578-7195">
                    <Phone className="h-5 w-5 mr-2" />
                    703-578-7195
                  </a>
                </Button>
                <Button variant="outline" size="lg" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                  <Calendar className="h-5 w-5 mr-2" />
                  Contact Us
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Faith-based mission statement */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm max-w-2xl mx-auto">
            Goodwin Living Health is a <strong>faith-based, not-for-profit organization</strong> powered by purpose. 
            Our mission is to support, honor, and uplift the lives of older adults and those who care for them.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VirtualTours;
